import { useState, useCallback, useEffect, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import MapExplorer from "@/components/MapExplorer";
import { NOT_INTERESTED_REASONS } from "@/lib/format";
import { toast } from "sonner";
import GarageCard from "@/components/GarageCard";
import GooglePlaceSearch from "@/components/GooglePlaceSearch";
import AreaFilter from "@/components/AreaFilter";
import { useCheckin } from "@/components/CheckinProvider";
import { useConfirm } from "@/components/ConfirmProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, RefreshCw, List, Map as MapIcon, ThumbsDown } from "lucide-react";

export default function NearMePanel({ onOpenDetail }) {
  const checkin = useCheckin();
  const confirm = useConfirm();
  const [garages, setGarages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [radius, setRadius] = useState(5);
  const [view, setView] = useState("list");
  const [homeBase, setHomeBase] = useState(null);
  const [sortBy, setSortBy] = useState("distance");
  const [center, setCenter] = useState(null);      // {lat,lng} when searching a place; null = my GPS
  const [placeLabel, setPlaceLabel] = useState(""); // 3.5 — name of the searched place
  const [ni, setNi] = useState(null);
  const [niReason, setNiReason] = useState("");
  const [areaSel, setAreaSel] = useState([]); // 4.2 — filter nearby results by area

  const convert = async (g) => {
    if ((g.visit_count || 0) < 1) { toast.error("Record a visit before creating the WhatsApp group"); return; }
    if (!(await confirm({ title: "Convert to My garages", description: `Move "${g.name}" to My garages? This marks the WhatsApp group as created.`, confirmText: "Convert" }))) return;
    try { await api.patch(`/garages/${g.id}`, { status: "group_created" }); toast.success(`${g.name} moved to My garages`); reload(radius); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveNi = async () => {
    if (!niReason) { toast.error("Pick a reason"); return; }
    try { await api.patch(`/garages/${ni.id}`, { status: "not_interested", not_interested_reason: niReason }); toast.success(`${ni.name} marked not interested`); setNi(null); setNiReason(""); reload(radius); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sorted = useMemo(() => {
    const r = [...garages];
    if (sortBy === "score") r.sort((a, b) => (b.opportunity_score || 0) - (a.opportunity_score || 0));
    else if (sortBy === "visit") r.sort((a, b) => new Date(b.last_visit_date || 0) - new Date(a.last_visit_date || 0));
    else r.sort((a, b) => (a.distance_km ?? 9e9) - (b.distance_km ?? 9e9));
    return r;
  }, [garages, sortBy]);
  const shown = useMemo(() => (areaSel.length ? sorted.filter((g) => areaSel.includes(g.area)) : sorted), [sorted, areaSel]);

  const fetchNearby = useCallback(async (lat, lng, r) => {
    setLoading(true);
    try {
      const { data } = await api.get("/garages/nearby", { params: { lat, lng, radius_km: r } });
      setGarages(data);
      if (data.length === 0) toast("Nothing within this radius — try a bigger one");
    } catch { toast.error("Could not load nearby garages"); }
    setLoading(false);
  }, []);

  const load = useCallback((r) => {
    if (!navigator.geolocation) { toast.error("Location unavailable"); return; }
    setCenter(null); setPlaceLabel("");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchNearby(pos.coords.latitude, pos.coords.longitude, r),
      () => { setLoading(false); toast.error("Turn on location to use this"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [fetchNearby]);

  // 3.5 — pick a place from Google autocomplete and search around it instead of GPS
  const pickPlace = (p) => {
    if (p.latitude == null) { toast.error("That place has no location"); return; }
    setCenter({ lat: p.latitude, lng: p.longitude });
    setPlaceLabel(p.name || "selected place");
    fetchNearby(p.latitude, p.longitude, radius);
  };
  const reload = (r) => (center ? fetchNearby(center.lat, center.lng, r) : load(r));

  useEffect(() => { load(radius); /* eslint-disable-next-line */ }, []);
  useEffect(() => { api.get("/settings/home-base").then((r) => setHomeBase(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const h = () => reload(radius);
    window.addEventListener("jam:data", h);
    return () => window.removeEventListener("jam:data", h);
    // eslint-disable-next-line
  }, [radius, center]);

  return (
    <div data-testid="near-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1E3A8A]">Around me</h1>
          <p className="text-sm text-[#6B7280] mt-1">{center ? <>Around <b>{placeLabel}</b></> : "Around your current location"} — from your own database, no API cost · {shown.length} found</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-[#D1D5DB] overflow-hidden">
            <button onClick={() => setView("list")} data-testid="near-view-list"
              className={`h-11 px-3 text-sm font-bold flex items-center gap-1.5 ${view === "list" ? "bg-[#1E3A8A] text-white" : "bg-white text-[#374151]"}`}><List className="w-4 h-4" /> List</button>
            <button onClick={() => setView("map")} data-testid="near-view-map"
              className={`h-11 px-3 text-sm font-bold flex items-center gap-1.5 ${view === "map" ? "bg-[#1E3A8A] text-white" : "bg-white text-[#374151]"}`}><MapIcon className="w-4 h-4" /> Map</button>
          </div>
          <button onClick={() => reload(radius)} data-testid="refresh-near"
            className="h-11 w-11 rounded-lg border border-[#D1D5DB] bg-white flex items-center justify-center text-[#6B7280]">
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* 3.5 — search any place, or snap back to GPS */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold uppercase text-[#6B7280]" data-testid="near-mode">
            {center ? `Around ${placeLabel}` : "Around me (GPS)"}
          </span>
          {center && (
            <button onClick={() => load(radius)} data-testid="near-use-my-location"
              className="text-xs font-bold text-[#1E3A8A]">Use my location</button>
          )}
        </div>
        <GooglePlaceSearch onSelect={pickPlace} placeholder="Search a place, e.g. Al Quoz Industrial 4…" />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-3 mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-bold uppercase text-[#6B7280]">How far around me</span>
          <span className="text-sm font-black text-[#1E3A8A]" data-testid="near-radius-value">{radius} km</span>
        </div>
        <input type="range" min="1" max="30" step="1" value={radius} data-testid="near-radius"
          onChange={(e) => setRadius(Number(e.target.value))}
          onMouseUp={() => reload(radius)} onTouchEnd={() => reload(radius)}
          className="w-full h-8 accent-[#1E3A8A]" />
      </div>

      {/* 4.2 — filter the nearby results by area (search + emirate grouping) */}
      {garages.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <AreaFilter garages={garages} selected={areaSel} onChange={setAreaSel} testId="near-area" />
          {areaSel.length > 0 && (
            <span className="text-xs text-[#6B7280]" data-testid="near-area-count">{shown.length} in {areaSel.length} area{areaSel.length > 1 ? "s" : ""}</span>
          )}
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div>
      ) : view === "map" ? (
        <MapExplorer garages={shown} homeBase={homeBase} onOpenDetail={onOpenDetail} testId="near-explorer" />
      ) : shown.length === 0 ? (
        <div className="py-16 text-center text-[#6B7280] bg-white border border-[#E5E7EB] rounded-lg">{areaSel.length ? "No garages in the selected areas within this radius." : `Nothing within ${radius} km. Widen the radius or scan a new area.`}</div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold uppercase text-[#6B7280]">Sort</span>
            {[["distance", "Distance"], ["score", "Prospect score"], ["visit", "Last visit"]].map(([k, l]) => (
              <button key={k} onClick={() => setSortBy(k)} data-testid={`near-sort-${k}`}
                className={`h-9 px-3 rounded-md text-sm font-bold ${sortBy === k ? "bg-[#1E3A8A] text-white" : "bg-white border border-[#D1D5DB] text-[#374151]"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="near-list">
            {shown.map((g) => (
              <GarageCard key={g.id} g={g} showBadge onOpen={onOpenDetail} onCheckin={checkin}
                onConvert={convert} onOrder={(x) => onOpenDetail(x.id)} onNotInterested={(x) => { setNi(x); setNiReason(""); }} />
            ))}
          </div>
        </>
      )}

      <Dialog open={!!ni} onOpenChange={(v) => { if (!v) { setNi(null); setNiReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="near-ni-dialog">
          <DialogHeader><DialogTitle className="text-lg font-black text-[#1E3A8A]">Mark not interested</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280] -mt-2">Why isn't <b>{ni?.name}</b> a fit?</p>
          <div className="space-y-2 mt-1">
            {NOT_INTERESTED_REASONS.map((r) => (
              <label key={r.value} data-testid={`near-ni-${r.value}`}
                className={`flex items-center gap-3 h-12 px-3 rounded-lg border cursor-pointer ${niReason === r.value ? "border-[#1E3A8A] bg-[#EEF2FF]" : "border-[#E5E7EB]"}`}>
                <input type="radio" name="near-ni" checked={niReason === r.value} onChange={() => setNiReason(r.value)} />
                <span className="font-medium text-sm text-[#111827]">{r.label}</span>
              </label>
            ))}
          </div>
          <button onClick={saveNi} data-testid="near-ni-save"
            className="w-full h-12 rounded-lg bg-[#B91C1C] text-white font-bold flex items-center justify-center gap-2 mt-2">
            <ThumbsDown className="w-4 h-4" /> Mark not interested
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
